/**
 * Indicadores de carga reutilizables.
 *
 *  - GlobalLoadingBar : barra fija arriba de TODO; visible ante cualquier query
 *                       fetcheando (primera carga o refetch). Se monta 1 vez en
 *                       el Layout — resuelve el "no veo cuándo está cargando".
 *  - Spinner          : action chiquita (botón, inline).
 *  - Skeleton         : caja primitiva con shimmer (componer a medida).
 *  - TableSkeleton    : tabla aún sin data — placeholder con shimmer.
 *  - CardsSkeleton    : grilla de cards (dashboards / stats).
 *  - LinesSkeleton    : líneas de texto (detalles / formularios).
 *  - RefetchBar       : data visible, refetch en background — banda fina arriba.
 *
 * El CSS vive en `styles/globals.css` (sección "Loaders").
 */
import type { CSSProperties } from 'react';
import { useIsFetching } from '@tanstack/react-query';

/**
 * Barra de carga GLOBAL: visible mientras haya CUALQUIER query fetcheando
 * (primera carga o refetch de fondo del poll). Se monta una sola vez en el
 * Layout. Resuelve de forma transversal el "no veo cuándo está cargando".
 */
export function GlobalLoadingBar() {
  const fetching = useIsFetching();
  if (!fetching) return null;
  return <div className="globalLoadingBar" role="status" aria-label="Cargando" />;
}

/** Caja skeleton primitiva (un bloque con shimmer). Para componer placeholders. */
export function Skeleton({
  width,
  height = 12,
  radius = 6,
  className,
  style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={className ? `skeleton ${className}` : 'skeleton'}
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

/** Grilla de cards skeleton — para dashboards / paneles de stats. */
export function CardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      aria-busy="true"
      aria-label="Cargando"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 16,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span className="skeleton" style={{ width: 40, height: 40, borderRadius: 12 }} />
          <span className="skeleton" style={{ width: '55%', height: 28 }} />
          <span className="skeleton" style={{ width: '85%', height: 12 }} />
        </div>
      ))}
    </div>
  );
}

/** Líneas de texto skeleton — para detalles, formularios, párrafos. */
export function LinesSkeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div
      aria-busy="true"
      aria-label="Cargando"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {Array.from({ length: lines }).map((_, i) => (
        <span key={i} className="skeleton" style={{ height: 14, width: `${90 - i * 8}%` }} />
      ))}
    </div>
  );
}

/** Spinner circular inline. Tamaño y color heredados del contenedor. */
export function Spinner({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <span
      className={className ? `spinner ${className}` : 'spinner'}
      style={{ fontSize: size }}
      aria-label="Cargando"
      role="status"
    />
  );
}

/**
 * Placeholder de tabla con shimmer. `rows` controla cuántas líneas dibuja.
 * Se usa cuando aún no hay data (primera carga). Si ya hay data y se está
 * refetcheando, usar `<RefetchBar />` para no taparla.
 */
export function TableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div
      className="tableWrap"
      aria-busy="true"
      aria-label="Cargando registros"
      style={{ padding: '12px 16px' }}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '1px solid var(--color-outline)',
          }}
        >
          <span className="skeleton" style={{ width: 56, height: 18, flexShrink: 0 }} />
          <span className="skeleton" style={{ width: 70, height: 18, flexShrink: 0 }} />
          <span className="skeleton" style={{ width: 140, flex: 2 }} />
          <span className="skeleton" style={{ width: 90, flex: 1 }} />
          <span className="skeleton" style={{ width: 100, flex: 1 }} />
          <span className="skeleton" style={{ width: 110, flexShrink: 0 }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Banda fina animada — se muestra arriba del contenido cuando hay un
 * refetch en background (data ya visible). No tapa nada, solo indica que
 * algo está sucediendo. Pensada para top de cards o de la tabla.
 */
export function RefetchBar({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return <div className="refetchBar" aria-label="Actualizando datos" />;
}
